// Paket durumu (ücretsiz / deneme / ücretli / grace / salt okunur / kapalı) ve "Satın al" penceresi.
// Kaynak: GET /tenant/me → access. Açılışta, her 5 dakikada ve refreshPlan() ile yenilenir.
// `access` yoksa (eski sunucu ya da hata) kısıtlama yok sayılır.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Access, Feature, Plan } from '../../../supabase/functions/_shared/plan-access';
import { hasFeature as accessHasFeature, PLAN_LIMITS } from '../../../supabase/functions/_shared/plan-access';
import { tenantApi } from './api';
import { PlanLimitDialog } from '../components/PlanLimitDialog';

export const PLAN_LIMIT_EVENT = 'plan-limit';
export const PLAN_REFRESH_EVENT = 'plan-refresh';
const REFRESH_MS = 5 * 60 * 1000;
/** Abonelik kilidi 402'si geldiğinde durumu en çok bu sıklıkta yeniden sor */
const LOCK_REFRESH_MIN_MS = 30 * 1000;

export interface PlanContextValue {
  access: Access | null;
  hasFeature(f: Feature): boolean;
  openPaywall(feature: string, limit?: number): void;
  refreshPlan(): Promise<void>;
  /** /tenant/me'nin eski alanı — `access` yokken eski abonelik şeritleri için */
  subscriptionEndDate: string | null;
  /** İlk yükleme bitti mi (kapalı hesap yönlendirmesi bunu bekler) */
  loaded: boolean;
}

// Sağlayıcı dışında (ör. ödeme dönüş sayfaları MainLayout dışında): kısıtlama yok,
// refreshPlan olay yayar — açık bir sağlayıcı varsa o yeniler.
const FALLBACK: PlanContextValue = {
  access: null,
  hasFeature: () => true,
  openPaywall: (feature, limit) => {
    window.dispatchEvent(new CustomEvent(PLAN_LIMIT_EVENT, { detail: { feature, limit } }));
  },
  refreshPlan: async () => {
    window.dispatchEvent(new Event(PLAN_REFRESH_EVENT));
  },
  subscriptionEndDate: null,
  loaded: true,
};

const PlanContext = createContext<PlanContextValue>(FALLBACK);

export function usePlan(): PlanContextValue {
  return useContext(PlanContext);
}

/**
 * Yalnız geliştirmede: localStorage 'plan_access_override' = Access JSON'u (eksik alanlar doldurulur).
 * Örn. {"tier":"trial","daysLeft":2}. Kaldırınca sunucu değeri kullanılır. Üretimde hiç okunmaz.
 */
function readDevOverride(): Access | undefined {
  if (!import.meta.env.DEV) return undefined;
  try {
    const raw = localStorage.getItem('plan_access_override');
    if (!raw) return undefined;
    const o = JSON.parse(raw) || {};
    const tier = o.tier || 'paid';
    const plan: Plan = o.plan || (tier === 'free' ? 'free' : 'starter');
    return {
      tier,
      plan,
      limits: { ...PLAN_LIMITS[plan], ...(o.limits || {}) },
      endsAt: o.endsAt ?? null,
      daysLeft: o.daysLeft ?? null,
    } as Access;
  } catch {
    return undefined;
  }
}

/** 402 limit göndermediyse firmanın paket limiti; o da yoksa planLimitText ücretsiz sayıyı kullanır */
function limitFromAccess(access: Access | null, feature: string): number | undefined {
  if (!access) return undefined;
  const map: Record<string, number | null | undefined> = {
    users: access.limits.users,
    receipts: access.limits.receiptsPerMonth,
    customers: access.limits.customers,
    whatsapp_daily: access.limits.whatsappPerDay,
  };
  const v = map[feature];
  return typeof v === 'number' ? v : undefined;
}

export function PlanProvider({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const [access, setAccess] = useState<Access | null>(null);
  const [subscriptionEndDate, setSubscriptionEndDate] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [paywall, setPaywall] = useState<{ feature: string; limit?: number } | null>(null);
  const accessRef = useRef<Access | null>(null);
  const lastRefreshRef = useRef(0);

  const refreshPlan = useCallback(async () => {
    lastRefreshRef.current = Date.now();
    const override = readDevOverride();
    if (override) {
      accessRef.current = override;
      setAccess(override);
      setLoaded(true);
      return;
    }
    try {
      const me = await tenantApi.getMe();
      const next = me?.access ?? null;
      accessRef.current = next;
      setAccess(next);
      setSubscriptionEndDate(me?.subscriptionEndDate || null);
    } catch {
      // Ağ / sunucu hatası: son bilinen durum kalır
    } finally {
      setLoaded(true);
    }
  }, []);

  // Açılış + 5 dakikada bir + 'plan-refresh' olayı
  useEffect(() => {
    if (!enabled) return;
    refreshPlan();
    const timer = window.setInterval(refreshPlan, REFRESH_MS);
    const onRefresh = () => { refreshPlan(); };
    window.addEventListener(PLAN_REFRESH_EVENT, onRefresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(PLAN_REFRESH_EVENT, onRefresh);
    };
  }, [enabled, refreshPlan]);

  // 402 / ekran olayları → pencere. Açık pencere varken yenisi onun yerine geçmez (art arda istekler).
  useEffect(() => {
    const onLimit = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const feature = typeof d.feature === 'string' ? d.feature : '';
      if (!feature) return;
      const limit = typeof d.limit === 'number' ? d.limit : undefined;
      if (feature === 'SUBSCRIPTION_READ_ONLY' || feature === 'ACCOUNT_CLOSED') {
        // Sunucu durumu bizden önce değişmiş olabilir → şerit/yönlendirme için yeniden sor
        if (Date.now() - lastRefreshRef.current > LOCK_REFRESH_MIN_MS) refreshPlan();
        // Kapalı hesapta arka plan istekleri de 402 alır; şerit + yönlendirme zaten gösteriyor
        if (feature === 'ACCOUNT_CLOSED' && accessRef.current?.tier === 'closed') return;
      }
      setPaywall((prev) => prev ?? { feature, limit });
    };
    window.addEventListener(PLAN_LIMIT_EVENT, onLimit);
    return () => window.removeEventListener(PLAN_LIMIT_EVENT, onLimit);
  }, [refreshPlan]);

  const openPaywall = useCallback((feature: string, limit?: number) => {
    setPaywall({ feature, limit });
  }, []);

  const hasFeature = useCallback((f: Feature) => (access ? accessHasFeature(access, f) : true), [access]);

  const value = useMemo<PlanContextValue>(
    () => ({ access, hasFeature, openPaywall, refreshPlan, subscriptionEndDate, loaded }),
    [access, hasFeature, openPaywall, refreshPlan, subscriptionEndDate, loaded],
  );

  return (
    <PlanContext.Provider value={value}>
      {children}
      <PlanLimitDialog
        open={!!paywall}
        feature={paywall?.feature || ''}
        limit={paywall ? paywall.limit ?? limitFromAccess(access, paywall.feature) : undefined}
        onClose={() => setPaywall(null)}
      />
    </PlanContext.Provider>
  );
}
